/***
 * タブ機能を管理
 *
 * 仕様
 * - ist-js__tabs: 親
 * - ist-js__tabList: タブリスト
 * - ist-js__tab: タブ
 *   - data-active-more-link="true" がある場合、そのタブがクリックされたら、そのタブの次のタブをアクティブにする
 * - ist-js__tabPanel: パネル
 *
 * 使い方
 * <div class="ist-js__tabs">
 *   <div class="ist-js__tabList" role="tablist">
 *     <a class="ist-js__tab" role="tab">Tab 1</a>
 *     <a class="ist-js__tab" role="tab">Tab 2</a>
 *   </div>
 *   <div class="ist-js__tabPanel" role="tabpanel">Content 1</div>
 *   <div class="ist-js__tabPanel" role="tabpanel">Content 2</div>
 * </div>
 *
 * 追加仕様01
 * - ist-js__tab に 下記の属性がある場合、そのタブがクリックされたら、data-change-text-classに指定されたクラス名の要素のdata-change-text-valueを変更する
 *   - data-change-text-class
 *   - data-change-text-value
 *
 * 追加仕様02
 * - ist-js__tabs に data-max-height="true" がある場合、全てのタブの中から最も高いタブの高さを取得し、その高さになるようにタブの高さを調整する
 * - リサイズイベントを監視し、タブの高さを再調整
 * - ist-js__tabPanel の高さを揃えるのが主な目的です。
 *
 * 追加仕様03
 * - ist-js__tabList はヘッダー部分とフッター部分表示可能としたい。挙動についてはこれまでの挙動を踏襲する。ただし、ist-js__tabList は同期が必要。ウェブアクセシビリティのかを考慮すること。
 * <div class="ist-js__tabs">
 *   <div class="ist-js__tabList" role="tablist">
 *     <a class="ist-js__tab" role="tab">Tab 1</a>
 *     <a class="ist-js__tab" role="tab">Tab 2</a>
 *   </div>
 *
 *   <div class="ist-js__tabPanel" role="tabpanel">Content 1</div>
 *   <div class="ist-js__tabPanel" role="tabpanel">Content 2</div>
 *
 *   <div class="ist-js__tabList ist-js__tabList--footer" role="tablist">
 *     <a class="ist-js__tab" role="tab">Tab 1</a>
 *     <a class="ist-js__tab" role="tab">Tab 2</a>
 *   </div>
 * </div>
 */

import { v4 as uuidv4 } from 'uuid';

class TabError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TabError';
  }
}

export class Tabs {
  private static readonly CLASS_NAMES = {
    base: 'ist-js__tabs',
    tabList: 'ist-js__tabList',
    tab: 'ist-js__tab',
    tabPanel: 'ist-js__tabPanel',
    active: '--active',
    touched: '--touched',
    liveRegion: 'ist-js__tabLive',
    tabMoreLink: 'ist-js__tabMoreLink',
    tabMoreLinkAction: 'data-active-more-link',
    tabMoreLinkHidden: '--hidden',
    tabMoreLinkAnimated: '--animated',
    tabListFooter: 'ist-js__tabList--footer',
  } as const;

  private container: HTMLElement;
  private tabList: Element | null;
  private tabs: NodeListOf<HTMLElement>;
  private panels: NodeListOf<HTMLElement>;
  private liveRegion!: HTMLElement;
  private moreLinkAction: HTMLElement | null;
  private moreLink: HTMLElement | null;
  private resizeHandler?: () => void;

  constructor(container: HTMLElement, firstView = 0) {
    try {
      this.container = container;
      this.tabList = container.querySelector(`.${Tabs.CLASS_NAMES.tabList}`);
      this.panels = container.querySelectorAll(`.${Tabs.CLASS_NAMES.tabPanel}`);

      // メインのタブリストからタブを取得
      const mainTabList = container.querySelector(
        `.${Tabs.CLASS_NAMES.tabList}:not(.${Tabs.CLASS_NAMES.tabListFooter})`
      );
      if (!mainTabList) throw new TabError('Main tablist not found');

      const mainTabs = Array.from(
        mainTabList.querySelectorAll(`.${Tabs.CLASS_NAMES.tab}`)
      ) as HTMLElement[];
      if (mainTabs.length === 0)
        throw new TabError('No tabs found in main tablist');

      // フッターのタブリストが存在する場合、それらのタブも取得
      const footerTabList = container.querySelector(
        `.${Tabs.CLASS_NAMES.tabList}.${Tabs.CLASS_NAMES.tabListFooter}`
      );
      if (footerTabList) {
        const footerTabs = Array.from(
          footerTabList.querySelectorAll(`.${Tabs.CLASS_NAMES.tab}`)
        ) as HTMLElement[];
        this.tabs = [
          ...mainTabs,
          ...footerTabs,
        ] as unknown as NodeListOf<HTMLElement>;
      } else {
        this.tabs = mainTabs as unknown as NodeListOf<HTMLElement>;
      }

      this.moreLinkAction = container.querySelector(
        `[${Tabs.CLASS_NAMES.tabMoreLinkAction}]`
      );
      this.moreLink = container.querySelector(
        `.${Tabs.CLASS_NAMES.tabMoreLink}`
      );

      if (this.validateElements()) {
        this.setupLiveRegion();
        const validFirstView = this.validateFirstView(firstView);
        this.init(validFirstView);
        this.setupTouchSupport();
      }
    } catch (error) {
      console.error('Unexpected error during tab initialization:', error);
      throw error;
    }
  }

  private setupLiveRegion(): void {
    this.liveRegion = document.createElement('div');
    this.liveRegion.className = Tabs.CLASS_NAMES.liveRegion;
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.style.position = 'absolute';
    this.liveRegion.style.width = '1px';
    this.liveRegion.style.height = '1px';
    this.liveRegion.style.padding = '0';
    this.liveRegion.style.margin = '-1px';
    this.liveRegion.style.overflow = 'hidden';
    this.liveRegion.style.clip = 'rect(0, 0, 0, 0)';
    this.liveRegion.style.whiteSpace = 'nowrap';
    this.liveRegion.style.border = '0';
    this.container.appendChild(this.liveRegion);
  }

  private validateFirstView(firstView: number): number {
    if (!Number.isInteger(firstView)) {
      console.warn('First view must be an integer. Defaulting to 0.');
      return 0;
    }

    if (firstView < 0) {
      console.warn('First view cannot be negative. Defaulting to 0.');
      return 0;
    }

    if (firstView >= this.tabs.length) {
      console.warn(
        `First view (${firstView}) is out of range. Defaulting to 0.`
      );
      return 0;
    }

    return firstView;
  }

  private validateElements(): boolean {
    if (!this.tabList) {
      throw new TabError(
        `Tablist element with class ${Tabs.CLASS_NAMES.tabList} not found`
      );
    }

    // ヘッダーのタブリストのタブのみを取得
    const headerTabs = this.tabList.querySelectorAll(
      `.${Tabs.CLASS_NAMES.tab}`
    );

    if (headerTabs.length === 0) {
      throw new TabError(
        `No tab elements with class ${Tabs.CLASS_NAMES.tab} found`
      );
    }

    if (this.panels.length === 0) {
      throw new TabError(
        `No panel elements with class ${Tabs.CLASS_NAMES.tabPanel} found`
      );
    }

    if (headerTabs.length !== this.panels.length) {
      throw new TabError(
        `Number of tabs (${headerTabs.length}) does not match number of panels (${this.panels.length})`
      );
    }

    return true;
  }

  private adjustPanelHeights(): void {
    const hasMaxHeightAttribute =
      this.container.getAttribute('data-max-height') === 'true';
    if (!hasMaxHeightAttribute) {
      return;
    }

    this.panels.forEach((panel) => {
      panel.style.height = '';
    });

    const heights = Array.from(this.panels).map((panel) => {
      const wasHidden = panel.hasAttribute('hidden');
      if (wasHidden) {
        panel.style.visibility = 'hidden';
        panel.style.display = 'block';
        panel.removeAttribute('hidden');
      }

      const height = panel.getBoundingClientRect().height;

      if (wasHidden) {
        panel.style.visibility = '';
        panel.style.display = '';
        panel.setAttribute('hidden', '');
      }

      return height;
    });

    const maxHeight = Math.max(...heights);

    if (maxHeight > 0) {
      this.panels.forEach((panel) => {
        panel.style.height = `${maxHeight}px`;
      });
    }
  }

  private setupResizeListener(): void {
    let resizeTimeout: number;

    const resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        this.adjustPanelHeights();
      }, 250);
    };

    window.addEventListener('resize', resizeHandler);
    this.resizeHandler = resizeHandler;
  }

  private init(firstView: number): void {
    this.setupIds();
    this.setupAttributes();
    this.bindEvents();
    this.setupMoreLink();
    this.adjustPanelHeights();
    this.setupResizeListener();
    this.activateTab(firstView);
  }

  private setupMoreLink(): void {
    if (this.moreLinkAction && this.moreLink) {
      this.moreLink.classList.add(
        `${Tabs.CLASS_NAMES.tabMoreLink}${Tabs.CLASS_NAMES.tabMoreLinkHidden}`
      );
    }
  }

  private setupIds(): void {
    // メインのタブリストのタブのみを取得
    const mainTabList = this.container.querySelector(
      `.${Tabs.CLASS_NAMES.tabList}:not(.${Tabs.CLASS_NAMES.tabListFooter})`
    );
    if (!mainTabList) return;

    const mainTabs = mainTabList.querySelectorAll(`.${Tabs.CLASS_NAMES.tab}`);

    mainTabs.forEach((tab, index) => {
      const tabId = `${Tabs.CLASS_NAMES.base}-tab-${uuidv4()}`;
      const panelId = `${Tabs.CLASS_NAMES.base}-panel-${uuidv4()}`;

      if (this.panels[index]) {
        tab.id = tabId;
        this.panels[index].id = panelId;

        tab.setAttribute('aria-controls', panelId);
        this.panels[index].setAttribute('aria-labelledby', tabId);

        // フッターのタブと同期
        const footerTab = this.container.querySelector(
          `.${Tabs.CLASS_NAMES.tabListFooter} .${Tabs.CLASS_NAMES.tab}:nth-child(${index + 1})`
        );
        if (footerTab) {
          footerTab.id = `${tabId}-footer`;
          footerTab.setAttribute('aria-controls', panelId);
        }
      }
    });
  }

  private setupAttributes(): void {
    const allTabLists = this.container.querySelectorAll(
      `.${Tabs.CLASS_NAMES.tabList}`
    );

    allTabLists.forEach((tabList) => {
      tabList.setAttribute('role', 'tablist');
      if (tabList.classList.contains(Tabs.CLASS_NAMES.tabListFooter)) {
        tabList.setAttribute('aria-label', 'タブナビゲーション（フッター）');
      }
    });

    this.tabs.forEach((tab) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
    });

    this.panels.forEach((panel) => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('hidden', '');
    });
  }

  private setupTouchSupport(): void {
    this.tabs.forEach((tab) => {
      tab.addEventListener(
        'touchstart',
        () => {
          tab.classList.add(
            `${Tabs.CLASS_NAMES.tab}${Tabs.CLASS_NAMES.touched}`
          );
        },
        { passive: true }
      );

      tab.addEventListener(
        'touchend',
        () => {
          tab.classList.remove(
            `${Tabs.CLASS_NAMES.tab}${Tabs.CLASS_NAMES.touched}`
          );
        },
        { passive: true }
      );
    });
  }

  private bindEvents(): void {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (event) => this.handleClick(event, index));
      tab.addEventListener('keyup', (event) =>
        this.handleKeyNavigation(event, index)
      );
    });
  }

  private handleClick(event: Event, index: number): void {
    event.preventDefault();

    const clickedTab = event.currentTarget as HTMLElement;
    const parentTabList = clickedTab.closest(`.${Tabs.CLASS_NAMES.tabList}`);
    const isFooterTab = parentTabList?.classList.contains(
      `${Tabs.CLASS_NAMES.tabListFooter}`
    );

    if (isFooterTab && parentTabList) {
      const footerTabs = parentTabList.querySelectorAll(
        `.${Tabs.CLASS_NAMES.tab}`
      );
      const footerIndex = Array.from(footerTabs).indexOf(clickedTab);

      // まずスクロール処理を開始
      this.container.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // スクロール完了を待ってからタブ切り替えとフォーカス
      setTimeout(() => {
        this.activateTab(footerIndex);
        const mainTabList = this.container.querySelector(
          `.${Tabs.CLASS_NAMES.tabList}:not(.${Tabs.CLASS_NAMES.tabListFooter})`
        );
        const targetTab = mainTabList?.querySelector(
          `.${Tabs.CLASS_NAMES.tab}:nth-child(${footerIndex + 1})`
        ) as HTMLElement;
        targetTab?.focus();
      }, 500);
    } else {
      this.activateTab(index);
    }
  }

  private handleKeyNavigation(
    event: KeyboardEvent,
    currentIndex: number
  ): void {
    const isVertical =
      this.tabList?.getAttribute('aria-orientation') === 'vertical';
    const parentTabList = (event.currentTarget as HTMLElement).closest(
      `.${Tabs.CLASS_NAMES.tabList}`
    );
    const isFooterTab = parentTabList?.classList.contains(
      `${Tabs.CLASS_NAMES.tabListFooter}`
    );

    const keyMap: { [key: string]: number } = {
      [isVertical ? 'ArrowUp' : 'ArrowLeft']: currentIndex - 1,
      [isVertical ? 'ArrowDown' : 'ArrowRight']: currentIndex + 1,
      Home: 0,
      End: this.panels.length - 1,
    };

    const newIndex = keyMap[event.key];
    if (newIndex !== undefined) {
      event.preventDefault();
      const targetIndex =
        newIndex < 0
          ? this.panels.length - 1
          : newIndex >= this.panels.length
            ? 0
            : newIndex;

      if (isFooterTab) {
        this.container.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }

      this.activateTab(targetIndex);

      // フォーカスを適切なタブに移動
      const tabList = isFooterTab ? parentTabList : this.tabList;
      const targetTab = tabList?.querySelector(
        `.${Tabs.CLASS_NAMES.tab}:nth-child(${targetIndex + 1})`
      );
      (targetTab as HTMLElement)?.focus();
    }
  }

  private activateTab(index: number): void {
    const mainTabList = this.container.querySelector(
      `.${Tabs.CLASS_NAMES.tabList}:not(.${Tabs.CLASS_NAMES.tabListFooter})`
    );
    if (!mainTabList) return;

    const mainTabs = mainTabList.querySelectorAll(`.${Tabs.CLASS_NAMES.tab}`);
    const footerTabList = this.container.querySelector(
      `.${Tabs.CLASS_NAMES.tabList}.${Tabs.CLASS_NAMES.tabListFooter}`
    );
    const footerTabs = footerTabList?.querySelectorAll(
      `.${Tabs.CLASS_NAMES.tab}`
    );

    [mainTabs, footerTabs].forEach((tabSet) => {
      if (!tabSet) return;

      tabSet.forEach((tab, i) => {
        const isActive = i === index;
        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        tab.classList.toggle(
          `${Tabs.CLASS_NAMES.tab}${Tabs.CLASS_NAMES.active}`,
          isActive
        );

        // === 追加仕様01の処理 ===
        if (isActive) {
          const changeTextClass = tab.getAttribute('data-change-text-class');
          const changeTextValue = tab.getAttribute('data-change-text-value');

          if (changeTextClass && changeTextValue) {
            const targetElements = this.container.querySelectorAll(
              `.${changeTextClass}`
            );
            targetElements.forEach((el) => {
              el.textContent = changeTextValue;
            });
          }
        }
      });
    });

    this.panels.forEach((panel, i) => {
      const isActive = i === index;
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
      panel.classList.toggle(
        `${Tabs.CLASS_NAMES.tabPanel}${Tabs.CLASS_NAMES.active}`,
        isActive
      );
    });
  }

  public destroy(): void {
    this.tabs.forEach((tab, index) => {
      tab.removeEventListener('click', (event) =>
        this.handleClick(event, index)
      );
      tab.removeEventListener('keyup', (event) =>
        this.handleKeyNavigation(event, index)
      );
      tab.removeEventListener('touchstart', () => {});
      tab.removeEventListener('touchend', () => {});
    });

    if (this.liveRegion && this.liveRegion.parentNode) {
      this.liveRegion.parentNode.removeChild(this.liveRegion);
    }

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }
}
